import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Create, GetEmployeeList, GetActiveLoansByEmployee } from "adminApi/LoanInstallmentApi/LoanInstallmentApi";
import { validateForm } from "utils/validation";
import { LoanInstallmentRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import AddLoanInstallmentForm from "./components/AddLoanInstallmentForm";
import fields from "../json/LoanInstallment.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";

const buildPayload = (formValues) => ({
    employee_id: formValues.employee_id,
    grand_loan_id: formValues.grand_loan_id,
    installment_amount: formValues.installment_amount,
    payment_amount: formValues.payment_amount,
    payment_date: formValues.payment_date,
    receiver_id: formValues.receiver_id,
    installment_no: formValues.installment_no,
    notes: formValues.notes,
});

const AddLoanInstallment = () => {
    const navigate = useNavigate();
    const { formdata } = fields;
    const [loanOptions, setLoanOptions] = useState([]);
    const [installmentNoOptions, setInstallmentNoOptions] = useState([]);
    const [loadingLoans, setLoadingLoans] = useState(false);

    // Initialize form values from JSON fields
    const initialValues = formdata.reduce(
        (acc, field) => ({ ...acc, [field.name]: "" }),
        {},
    );

    const { values, errors, handleChange, setErrors, setValues } = useFormChange(
        initialValues,
        LoanInstallmentRules,
    );

    const fetchTakers = React.useCallback(() => GetEmployeeList("taker"), []);
    const fetchReceivers = React.useCallback(() => GetEmployeeList(), []);

    const { data: employeeOptions, loading: loadingEmployees } =
        useFetchAllData(fetchTakers);

    const { data: receiverOptions, loading: loadingReceivers } =
        useFetchAllData(fetchReceivers);

    // Fetch active loans when employee changes
    useEffect(() => {
        const fetchLoans = async () => {
            if (!values.employee_id) {
                setLoanOptions([]);
                return;
            }
            setLoadingLoans(true);
            try {
                const response = await GetActiveLoansByEmployee(values.employee_id);
                if (response.data.success) {
                    setLoanOptions(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching loans:", error);
            } finally {
                setLoadingLoans(false);
            }
        };

        fetchLoans();
    }, [values.employee_id]);

    // Update installment amount when loan changes
    useEffect(() => {
        if (!values.grand_loan_id || loanOptions.length === 0) {
            setValues((prev) => ({ ...prev, installment_amount: "" }));
            return;
        }
        const selectedLoan = loanOptions.find(
            (loan) => String(loan.id) === String(values.grand_loan_id)
        );
        if (selectedLoan) {
            setValues((prev) => ({
                ...prev,
                installment_amount: selectedLoan.installment_amount,
                payment_amount: selectedLoan.installment_amount,
                installment_no: Number(selectedLoan.paid_installments) + 1
            }));
        }
    }, [values.grand_loan_id, loanOptions, setValues]);

    // Form submit
    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            return await Create({ data: buildPayload(formValues) });
        },
        (vals) => {
            const err = validateForm(vals, LoanInstallmentRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Loan installment paid successfully",
    );

    return (
        <>
            {(loadingSubmit || loadingEmployees || loadingReceivers || loadingLoans) && <Spinner isSpinner={true} />}
            <AddLoanInstallmentForm
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loadingSubmit}
                employeeOptions={employeeOptions}
                receiverOptions={receiverOptions}
                loanOptions={loanOptions}
            />
        </>
    );
};

export default AddLoanInstallment;
